import { defineStore } from 'pinia'
import Pusher, { Channel } from 'pusher-js'

interface MaintenanceUpdate {
  id: string
  title: string
  description: string
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled'
  startTime: Date
  endTime: Date
  affectedServices: string[]
  createdAt: Date
  updatedAt: Date
}

export const useMaintenanceStore = defineStore('maintenance', {
  state: () => ({
    pusher: null as Pusher | null,
    channel: null as Channel | null,
    updates: [] as MaintenanceUpdate[],
    isLoading: false,
    error: null as string | null,
    currentMaintenanceId: null as string | null,
  }),

  actions: {
    // Initialize Pusher connection
    initializePusher(apiKey: string, cluster: string, authEndpoint: string) {
      if (this.pusher) return // Already initialized

      this.pusher = new Pusher(apiKey, {
        cluster,
        authEndpoint,
        auth: {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        },
      })

      // Bind to connection state changes
      this.pusher.connection.bind('state_change', (states: any) => {
        console.log('Pusher connection state changed:', states.current)
      })
    },

    // Subscribe to the maintenance channel
    subscribeToMaintenanceChannel() {
      if (!this.pusher) {
        throw new Error('Pusher not initialized')
      }

      // Unsubscribe from previous channel if exists
      if (this.channel) {
        this.unsubscribeFromChannel()
      }

      // Subscribe to the public maintenance channel
      this.channel = this.pusher.subscribe('my-channel')

      // Bind to maintenance events
      this.channel.bind('my-event', (data: MaintenanceUpdate) => {
        this.handleMaintenanceUpdate(data)
      })

      // You could add more event bindings here if needed
      // this.channel.bind('maintenance-started', ...)
      // this.channel.bind('maintenance-completed', ...)
    },

    // Unsubscribe from current channel
    unsubscribeFromChannel() {
      if (this.channel) {
        this.channel.unbind_all()
        this.channel.unsubscribe()
        this.channel = null
      }
    },

    // Handle incoming maintenance updates
    handleMaintenanceUpdate(update: MaintenanceUpdate) {
      const existingIndex = this.updates.findIndex((u) => u.id === update.id)

      if (existingIndex >= 0) {
        // Update existing maintenance record
        this.updates[existingIndex] = update
      } else {
        // Add new maintenance record
        this.updates.push(update)
      }

      // Sort updates by start time (most recent first)
      this.sortUpdates()
    },

    // Sort updates by start time (most recent first)
    sortUpdates() {
      this.updates.sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime())
    },

    // Fetch all maintenance updates
    async fetchMaintenanceUpdates() {
      this.isLoading = true
      this.error = null

      try {
        const response = await fetch('https://your-api-endpoint.com/maintenance', {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        })

        if (!response.ok) {
          throw new Error('Failed to fetch maintenance updates')
        }

        const updates = await response.json()
        this.updates = updates
        this.sortUpdates()
      } catch (err) {
        this.error = err instanceof Error ? err.message : 'Failed to fetch maintenance updates'
        throw err
      } finally {
        this.isLoading = false
      }
    },

    // Fetch specific maintenance by ID
    async fetchMaintenanceById(maintenanceId: string) {
      this.isLoading = true
      this.error = null
      this.currentMaintenanceId = maintenanceId

      try {
        const response = await fetch(`https://your-api-endpoint.com/maintenance/${maintenanceId}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        })

        if (!response.ok) {
          throw new Error('Failed to fetch maintenance details')
        }

        const maintenance = await response.json()
        this.handleMaintenanceUpdate(maintenance)
      } catch (err) {
        this.error = err instanceof Error ? err.message : 'Failed to fetch maintenance details'
        throw err
      } finally {
        this.isLoading = false
      }
    },

    // Clean up on logout or when no longer needed
    cleanup() {
      this.unsubscribeFromChannel()
      if (this.pusher) {
        this.pusher.disconnect()
        this.pusher = null
      }
      this.updates = []
      this.currentMaintenanceId = null
    },
  },

  getters: {
    // Get all maintenance updates
    getMaintenanceUpdates: (state) => state.updates,

    // Get active maintenance (in_progress or scheduled)
    getActiveMaintenance: (state) =>
      state.updates.filter((u) => u.status === 'in_progress' || u.status === 'scheduled'),

    // Get completed maintenance
    getCompletedMaintenance: (state) => state.updates.filter((u) => u.status === 'completed'),

    // Get maintenance by ID
    getMaintenanceById: (state) => (id: string) => state.updates.find((u) => u.id === id),

    // Loading state
    isLoadingMaintenance: (state) => state.isLoading,

    // Error state
    getError: (state) => state.error,
  },
})
